import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Heart, Activity, MessageSquare, Star } from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toAuthUrl } from "@/components/PhotoUpload";
import { useAuth } from "@/contexts/AuthContext";

const INJURY_STATUS = [
  { value: "en_cours", label: "En cours de soin" },
  { value: "retabli", label: "Rétablie" },
];

export default function PlayerDetailDialog({ playerId, open, onClose, onChanged }) {
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [injOpen, setInjOpen] = useState(false);
  const [obsOpen, setObsOpen] = useState(false);
  const [injForm, setInjForm] = useState({
    date: new Date().toISOString().slice(0, 10), type: "", description: "",
    duration_days: "", recovery_status: "en_cours",
  });
  const [obsForm, setObsForm] = useState({
    date: new Date().toISOString().slice(0, 10), author: user?.full_name || "",
    content: "", rating: 4,
  });

  const load = async () => {
    if (!playerId) return;
    const { data } = await api.get(`/players/${playerId}`);
    setPlayer(data);
  };
  useEffect(() => { if (open) load(); }, [playerId, open]);

  const addInjury = async () => {
    try {
      await api.post(`/players/${playerId}/injuries`, {
        ...injForm,
        duration_days: injForm.duration_days ? Number(injForm.duration_days) : null,
      });
      toast.success("Blessure enregistrée");
      setInjOpen(false);
      setInjForm({ date: new Date().toISOString().slice(0, 10), type: "", description: "", duration_days: "", recovery_status: "en_cours" });
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delInjury = async (iid) => {
    if (!window.confirm("Supprimer cette blessure ?")) return;
    await api.delete(`/players/${playerId}/injuries/${iid}`);
    load();
  };

  const addObs = async () => {
    try {
      await api.post(`/players/${playerId}/observations`, {
        ...obsForm,
        rating: Number(obsForm.rating),
      });
      toast.success("Observation ajoutée");
      setObsOpen(false);
      setObsForm({ date: new Date().toISOString().slice(0, 10), author: user?.full_name || "", content: "", rating: 4 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const delObs = async (oid) => {
    if (!window.confirm("Supprimer cette observation ?")) return;
    await api.delete(`/players/${playerId}/observations/${oid}`);
    load();
  };

  if (!player) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl"><div className="py-12 text-center text-slate-400">Chargement…</div></DialogContent>
      </Dialog>
    );
  }

  const injuries = player.injuries || [];
  const observations = player.progress_notes || [];

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="player-detail-dialog">
          <div className="px-8 py-6 bg-gradient-to-r from-emerald-50 to-white border-b border-slate-200">
            <div className="flex items-center gap-5">
              <div className="h-20 w-20 rounded-md border-2 border-emerald-200 overflow-hidden bg-white flex items-center justify-center shrink-0">
                {player.photo_url ? (
                  <img src={toAuthUrl(player.photo_url)} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="font-barlow font-bold text-3xl text-emerald-600">
                    {player.first_name?.[0]}{player.last_name?.[0]}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
                  {player.category} • {player.position || "—"}
                </div>
                <DialogHeader>
                  <DialogTitle className="font-barlow font-bold text-3xl tracking-tight">
                    {player.first_name} {player.last_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-slate-600">
                  <span>N° licence : <b>{player.license_number || "—"}</b></span>
                  <span>•</span>
                  <span>Maillot : <b>#{player.jersey_number ?? "—"}</b></span>
                  <span>•</span>
                  <span>{player.nationality}</span>
                </div>
              </div>
              <div className="text-right flex flex-col items-end gap-2">
                <Badge className={player.status === "active"
                  ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                  : player.status === "injured"
                  ? "bg-red-100 text-red-700 border-red-200"
                  : "bg-slate-100 text-slate-700 border-slate-200"}>
                  {player.status === "active" ? "Active" : player.status === "injured" ? "Blessée" : "Inactive"}
                </Badge>
                <a
                  href={`/joueurs/${player.id}/bilan`}
                  data-testid="open-bilan-link"
                  className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 underline"
                >
                  Voir le bilan complet →
                </a>
              </div>
            </div>
          </div>

          <div className="px-8 py-6">
            <Tabs defaultValue="info">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="info" data-testid="tab-info" className="data-[state=active]:bg-white">Informations</TabsTrigger>
                <TabsTrigger value="stats" data-testid="tab-stats" className="data-[state=active]:bg-white">Statistiques</TabsTrigger>
                <TabsTrigger value="injuries" data-testid="tab-injuries" className="data-[state=active]:bg-white">
                  Blessures ({injuries.length})
                </TabsTrigger>
                <TabsTrigger value="observations" data-testid="tab-observations" className="data-[state=active]:bg-white">
                  Observations ({observations.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-5">
                <div className="grid grid-cols-2 gap-3">
                  <InfoRow label="Date de naissance" value={formatDate(player.date_of_birth)} />
                  <InfoRow label="Nationalité" value={player.nationality} />
                  <InfoRow label="Taille" value={player.height_cm ? `${player.height_cm} cm` : "—"} />
                  <InfoRow label="Poids" value={player.weight_kg ? `${player.weight_kg} kg` : "—"} />
                  <InfoRow label="Poste" value={player.position} />
                  <InfoRow label="N° Maillot" value={player.jersey_number} />
                  <InfoRow label="N° Licence" value={player.license_number} />
                  <InfoRow label="Club précédent" value={player.previous_club} />
                </div>
                {player.coach_observations && (
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
                      Observations générales de l'entraîneur
                    </div>
                    <p className="text-sm text-slate-700 italic">{player.coach_observations}</p>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="stats" className="mt-5">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatBox label="Matchs joués" value={player.matches_played || 0} />
                  <StatBox label="Points" value={player.points || 0} />
                  <StatBox label="Passes" value={player.assists || 0} />
                  <StatBox label="Rebonds" value={player.rebounds || 0} />
                  <StatBox label="Contres" value={player.blocks || 0} />
                  <StatBox label="Pts / match"
                    value={player.matches_played ? (player.points / player.matches_played).toFixed(1) : "—"} />
                </div>
              </TabsContent>

              <TabsContent value="injuries" className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Historique des blessures
                  </div>
                  <Button size="sm" onClick={() => setInjOpen(true)} data-testid="add-injury-button"
                    className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                  </Button>
                </div>
                {injuries.length === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center border border-slate-100 rounded-md">
                    Aucune blessure enregistrée. 🎉
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...injuries].reverse().map((inj) => (
                      <div key={inj.id} data-testid={`injury-${inj.id}`}
                        className="border border-slate-200 rounded-md p-4 flex items-start gap-3">
                        <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${
                          inj.recovery_status === "retabli"
                            ? "bg-emerald-50 border border-emerald-200 text-emerald-700"
                            : "bg-red-50 border border-red-200 text-red-700"}`}>
                          <Heart className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-semibold text-slate-900">{inj.type}</div>
                            <Badge className={inj.recovery_status === "retabli"
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-red-100 text-red-700 border-red-200"}>
                              {inj.recovery_status === "retabli" ? "Rétablie" : "En cours"}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {formatDate(inj.date)} {inj.duration_days ? `• Durée prévue : ${inj.duration_days} j` : ""}
                          </div>
                          {inj.description && <div className="text-sm text-slate-600 mt-2">{inj.description}</div>}
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => delInjury(inj.id)}
                          className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                          data-testid={`delete-injury-${inj.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="observations" className="mt-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Suivi de progression
                  </div>
                  <Button size="sm" onClick={() => setObsOpen(true)} data-testid="add-obs-button"
                    className="bg-emerald-600 hover:bg-emerald-700">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                  </Button>
                </div>
                {observations.length === 0 ? (
                  <div className="text-sm text-slate-400 py-8 text-center border border-slate-100 rounded-md">
                    Aucune observation pour le moment.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {[...observations].reverse().map((obs) => (
                      <div key={obs.id} data-testid={`obs-${obs.id}`}
                        className="border border-slate-200 rounded-md p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                              {obs.author?.[0] || "?"}
                            </div>
                            <div>
                              <div className="font-semibold text-sm text-slate-900">{obs.author}</div>
                              <div className="text-xs text-slate-500">{formatDate(obs.date)}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {obs.rating && (
                              <div className="flex gap-0.5">
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <Star key={n} className={`h-3.5 w-3.5 ${n <= obs.rating ? "text-amber-500 fill-amber-500" : "text-slate-200"}`} />
                                ))}
                              </div>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => delObs(obs.id)}
                              className="text-red-600 hover:bg-red-50 h-8 w-8 p-0"
                              data-testid={`delete-obs-${obs.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{obs.content}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add injury */}
      <Dialog open={injOpen} onOpenChange={setInjOpen}>
        <DialogContent className="max-w-lg" data-testid="injury-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Nouvelle blessure</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date" type="date" value={injForm.date}
              onChange={(v) => setInjForm({ ...injForm, date: v })} testid="injury-date" />
            <Field label="Type / Localisation" value={injForm.type}
              onChange={(v) => setInjForm({ ...injForm, type: v })} required testid="injury-type" />
            <Field label="Durée estimée (jours)" type="number" value={injForm.duration_days}
              onChange={(v) => setInjForm({ ...injForm, duration_days: v })} testid="injury-duration" />
            <SelectField label="Statut" value={injForm.recovery_status}
              onChange={(v) => setInjForm({ ...injForm, recovery_status: v })}
              options={INJURY_STATUS} testid="injury-status" />
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">Description</Label>
              <Textarea data-testid="injury-description" value={injForm.description}
                onChange={(e) => setInjForm({ ...injForm, description: e.target.value })} rows={3}
                placeholder="Circonstances, prise en charge médicale..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setInjOpen(false)}>Annuler</Button>
            <Button onClick={addInjury} data-testid="save-injury-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add observation */}
      <Dialog open={obsOpen} onOpenChange={setObsOpen}>
        <DialogContent className="max-w-lg" data-testid="obs-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">Nouvelle observation</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date" type="date" value={obsForm.date}
              onChange={(v) => setObsForm({ ...obsForm, date: v })} testid="obs-date" />
            <Field label="Auteur" value={obsForm.author}
              onChange={(v) => setObsForm({ ...obsForm, author: v })} testid="obs-author" />
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">Note (1-5)</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setObsForm({ ...obsForm, rating: n })}
                    data-testid={`rating-${n}`}>
                    <Star className={`h-7 w-7 transition-colors ${n <= obsForm.rating ? "text-amber-500 fill-amber-500" : "text-slate-200 hover:text-amber-300"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">Observation</Label>
              <Textarea data-testid="obs-content" value={obsForm.content} required
                onChange={(e) => setObsForm({ ...obsForm, content: e.target.value })} rows={4}
                placeholder="Progression, points forts, axes de travail..." />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setObsOpen(false)}>Annuler</Button>
            <Button onClick={addObs} data-testid="save-obs-button" className="bg-emerald-600 hover:bg-emerald-700">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-3 border-b border-slate-100 pb-2">
      <span className="text-xs uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-900 text-right">{value || "—"}</span>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-4 text-center">
      <div className="font-barlow font-bold text-2xl text-slate-900 leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}
